import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Tag, ArrowLeft, Share2 } from 'lucide-react';

const TransfertsAeroportArticle = () => {
  const handleShare = async () => {
    try {
      await navigator.share({
        title: "Transferts Aéroport Premium : L'Excellence Selon Elite Transfert",
        text: "Découvrez comment nos services de transfert aéroport premium transforment votre expérience de voyage dès votre arrivée.",
        url: window.location.href
      });
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback : copier le lien dans le presse-papier
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papier');
    }
  };

  return (
    <article className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Transferts Aéroport Premium | Elite Transfert</title>
        <meta name="description" content="Découvrez comment nos services de transfert aéroport premium transforment votre expérience de voyage dès votre arrivée. Confort, ponctualité et service personnalisé pour tous vos déplacements." />
        <meta name="keywords" content="transfert aéroport, vtc premium, chauffeur privé, transport luxe, elite transfert" />
      </Helmet>

      {/* Article Header */}
      <header className="bg-gradient-to-b from-gray-900 to-black pt-12 pb-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center text-[#E5C13B] hover:text-white mb-8 transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux articles
          </Link>

          <div className="aspect-video w-full rounded-xl overflow-hidden mb-8 shadow-2xl">
            <img
              src="/images/blog/airport-guide.jpg"
              alt="Transferts Aéroport Premium"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-4">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <time>2 mars 2025</time>
            </div>
            <div className="flex items-center gap-1.5">
              <Tag className="h-4 w-4" />
              <span>Services Premium</span>
            </div>
            <button
              onClick={handleShare}
              className="ml-auto inline-flex items-center gap-1.5 text-[#E5C13B] hover:text-white"
            >
              <Share2 className="h-4 w-4" />
              Partager
            </button>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-[#E5C13B] leading-tight">
            Transferts Aéroport Premium : L'Excellence Selon Elite Transfert
          </h1>

          <div className="flex flex-wrap gap-2 mb-8">
            {['Transfert Aéroport', 'Service VTC', 'Voyage d\'Affaires', 'Confort Premium'].map(tag => (
              <span
                key={tag}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-[#E5C13B]"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </header>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="article-content space-y-8">
          <p className="text-xl leading-relaxed text-gray-300 mb-8 border-l-4 border-[#E5C13B] pl-6 py-2">
            Le voyage commence bien avant d'arriver à destination. Chez Elite Transfert, nous comprenons que chaque détail compte dans l'expérience globale de votre déplacement, en particulier lorsqu'il s'agit des transferts aéroport.
          </p>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#E5C13B] mb-6 pb-2 border-b border-gray-800">L'Importance d'un Service de Transfert Aéroport Premium</h2>
            
            <p className="text-gray-300 mb-6 leading-relaxed">
              Après un long vol, la dernière chose dont vous avez besoin est de vous soucier de votre transport. C'est pourquoi notre service de transfert aéroport premium est conçu pour éliminer tout stress et vous offrir une transition en douceur entre l'aéroport et votre destination.
            </p>
            
            <div className="my-8 rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/images/blog/vtc-luxury.jpg" 
                alt="Chauffeur Elite Transfert accueillant un client" 
                className="w-full h-auto"
              />
              <p className="text-sm text-gray-400 mt-2 text-center italic">Notre chauffeur vous accueille dès votre arrivée avec une plaque nominative</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#E5C13B] mb-6 pb-2 border-b border-gray-800">Ce Qui Distingue Elite Transfert</h2>
            
            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-[#E5C13B] mb-4">1. Une Flotte Premium</h3>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Notre flotte se compose exclusivement de véhicules haut de gamme, méticuleusement entretenus pour garantir votre confort et votre sécurité. Chaque véhicule est équipé d'aménagements luxueux pour rendre votre trajet aussi agréable que possible.
              </p>
              
              <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-6">
                <li>Mercedes-Benz Classe S et Classe V</li>
                <li>BMW Série 7</li>
                <li>Audi A8</li>
                <li>Tesla Model S et Model X</li>
              </ul>
            </div>
            
            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-[#E5C13B] mb-4">2. Des Chauffeurs d'Excellence</h3>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Nos chauffeurs ne sont pas de simples conducteurs. Ce sont des professionnels formés pour offrir un service irréprochable. Discrets, ponctuels et attentifs à vos besoins, ils incarnent l'excellence du service Elite Transfert.
              </p>
            </div>
            
            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-[#E5C13B] mb-4">3. Un Service Personnalisé</h3>
              <p className="text-gray-300 mb-4 leading-relaxed">
                Nous comprenons que chaque client a des besoins spécifiques. C'est pourquoi nous offrons un service entièrement personnalisable :
              </p>
              
              <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-6">
                <li>Suivi des vols en temps réel pour ajuster votre prise en charge en cas de retard</li>
                <li>Assistance bagages complète</li>
                <li>Sièges enfants disponibles sur demande</li>
                <li>Boissons rafraîchissantes à bord</li>
                <li>Wi-Fi gratuit pour rester connecté</li>
              </ul>
            </div>
            
            <div className="my-8 rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/images/blog/blog-article.jpeg" 
                alt="Intérieur luxueux d'une voiture Elite Transfert" 
                className="w-full h-auto"
              />
              <p className="text-sm text-gray-400 mt-2 text-center italic">L'intérieur luxueux de nos véhicules pour un confort optimal</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#E5C13B] mb-6 pb-2 border-b border-gray-800">Les Aéroports Desservis</h2>
            
            <p className="text-gray-300 mb-4 leading-relaxed">
              Elite Transfert assure des transferts depuis et vers tous les principaux aéroports de Paris et sa région :
            </p>
            
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-6">
              <li><strong className="text-white">Paris-Charles de Gaulle (CDG)</strong> - Le plus grand aéroport de France</li>
              <li><strong className="text-white">Paris-Orly (ORY)</strong> - Idéal pour les vols européens et domestiques</li>
              <li><strong className="text-white">Paris-Le Bourget</strong> - Spécialisé dans l'aviation d'affaires</li>
              <li><strong className="text-white">Aéroport de Beauvais-Tillé</strong> - Pour les compagnies low-cost</li>
            </ul>
          </section>
          
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#E5C13B] mb-6 pb-2 border-b border-gray-800">Services Spécialisés pour Voyageurs d'Affaires</h2>
            
            <p className="text-gray-300 mb-4 leading-relaxed">
              Les professionnels en déplacement apprécieront particulièrement nos services dédiés aux voyageurs d'affaires :
            </p>
            
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-6">
              <li>Facturation entreprise simplifiée</li>
              <li>Possibilité de réservation par un assistant</li>
              <li>Véhicules équipés pour travailler pendant le trajet</li>
              <li>Confidentialité garantie pour vos appels et discussions</li>
              <li>Service de conciergerie pour vos besoins additionnels</li>
            </ul>
          </section>
          
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#E5C13B] mb-6 pb-2 border-b border-gray-800">Comment Réserver Votre Transfert Aéroport</h2>
            
            <p className="text-gray-300 mb-4 leading-relaxed">
              La réservation de votre transfert aéroport avec Elite Transfert est simple et rapide :
            </p>
            
            <ol className="list-decimal pl-6 text-gray-300 space-y-2 mb-6">
              <li>Utilisez notre formulaire de réservation en ligne</li>
              <li>Précisez vos détails de vol et besoins spécifiques</li>
              <li>Recevez une confirmation immédiate</li>
              <li>Profitez d'un service sans souci le jour J</li>
            </ol>
            
            <div className="bg-gray-900 border border-[#E5C13B] rounded-xl p-8 my-8 shadow-lg">
              <h3 className="text-[#E5C13B] text-2xl font-semibold mb-4">Réservez Dès Maintenant</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Transformez votre expérience de voyage avec un transfert aéroport premium. Nos chauffeurs sont prêts à vous offrir un service d'exception.
              </p>
              <Link to="/" className="inline-block bg-[#E5C13B] text-black font-medium py-3 px-8 rounded-md hover:bg-[#d4b535] transition-colors duration-300 text-center">
                Réserver un Transfert
              </Link>
            </div>
          </section>
          
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#E5C13B] mb-6 pb-2 border-b border-gray-800">Témoignages Clients</h2>
            
            <div className="space-y-8">
              <blockquote className="italic border-l-4 border-[#E5C13B] pl-6 py-4 bg-gray-900 rounded-r-lg shadow-md">
                <p className="text-gray-300 mb-4">"J'utilise Elite Transfert pour tous mes déplacements professionnels. La ponctualité et le professionnalisme de leurs chauffeurs sont remarquables. Un service vraiment premium qui fait toute la différence."</p>
                <footer className="text-[#E5C13B] font-medium">— Sophie M., Directrice Marketing</footer>
              </blockquote>
              
              <blockquote className="italic border-l-4 border-[#E5C13B] pl-6 py-4 bg-gray-900 rounded-r-lg shadow-md">
                <p className="text-gray-300 mb-4">"Après un vol long-courrier, être accueilli par un chauffeur Elite Transfert est un véritable soulagement. Véhicule impeccable, chauffeur courtois, et une transition en douceur vers mon hôtel. Je recommande vivement."</p>
                <footer className="text-[#E5C13B] font-medium">— Thomas L., Entrepreneur</footer>
              </blockquote>
            </div>
          </section>
          
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-[#E5C13B] mb-6 pb-2 border-b border-gray-800">Conclusion</h2>
            
            <p className="text-gray-300 mb-4 leading-relaxed">
              Un transfert aéroport n'est pas simplement un trajet d'un point A à un point B. C'est une expérience qui peut définir le ton de votre voyage entier. Chez Elite Transfert, nous nous engageons à faire de cette expérience un moment de détente et de luxe, vous permettant d'arriver à destination reposé et prêt pour vos activités.
            </p>
            
            <p className="text-gray-300 mb-4 leading-relaxed">
              Faites confiance à Elite Transfert pour transformer vos transferts aéroport en une expérience premium digne de vos attentes les plus élevées.
            </p>
          </section>
          
          <div className="mt-16 pt-8 border-t border-gray-800">
            <h3 className="text-[#E5C13B] text-xl font-semibold mb-6">Articles Connexes</h3>
            <ul className="space-y-4">
              <li className="bg-gray-900 p-4 rounded-lg hover:bg-gray-800 transition-colors duration-200">
                <a href="#" className="text-gray-300 hover:text-[#E5C13B] transition-colors duration-200 block">
                  Les Avantages d'un Service VTC pour vos Déplacements d'Affaires
                </a>
              </li>
              <li className="bg-gray-900 p-4 rounded-lg hover:bg-gray-800 transition-colors duration-200">
                <a href="#" className="text-gray-300 hover:text-[#E5C13B] transition-colors duration-200 block">
                  Comment Choisir le Meilleur Service de Transport Premium
                </a>
              </li>
              <li className="bg-gray-900 p-4 rounded-lg hover:bg-gray-800 transition-colors duration-200">
                <a href="#" className="text-gray-300 hover:text-[#E5C13B] transition-colors duration-200 block">
                  5 Astuces pour un Voyage d'Affaires Sans Stress
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

export default TransfertsAeroportArticle;
